/* eslint-disable camelcase */

/* eslint-disable max-statements */
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import MessageInfo from '../../../components/messageInfo/MessageInfo';
import {
    useGetPrimaryApprovalWindowQuery,
    useUpdateInsertScorecardApprovalWindowMutation,
} from '../../../pages/compensation/compensationSlice';
import { selectCurrentEmpid } from '../../../pages/login/authSlice';
import { datetimeToDate, getNextDate, getPreviuosDate } from '../../../utils/dateFormat';
import { monthNames } from '../../../utils/typeConstants';
import { ScoreCardPropTypes } from '../types';
import './PrimaryApprovalDefault.scss';

interface ConfigurationLevel {
    name: string;
    btnLabel?: string;
    code: number;
}

const PrimaryApprovalDefault = ({
    inputYear,
    inputMonth,
    inputScorecardVersion,
    inputReadonly,
    via
}: ScoreCardPropTypes) => {
    const configurationLevels: ConfigurationLevel[] = [
        { name: 'Global', btnLabel: 'Activate Globally', code: 1 },
        { name: 'Parent Client', btnLabel: 'Activate for Parent Client', code: 2 },
    ];
    const [selectedConfigurationLevel, setSelectedConfigurationLevel] = useState(1);
    const [updateInsertScorecardApprovalWindow] = useUpdateInsertScorecardApprovalWindowMutation();
    const modifyByEmpId = useSelector(selectCurrentEmpid);
    const [associateStartDate, setAssociateStartDate] = useState<any>(null);
    const [associateEndDate, setAssociateEndDate] = useState<any>(null);
    const [tLStartDate, setTLStartDate] = useState<any>(null);
    const [tLEndDate, setTLEndDate] = useState<any>(null);
    const [mgrStartDate, setMgrStartDate] = useState<any>(null);
    const [mgrEndDate, setMgrEndDate] = useState<any>(null);
    const [compStartDate, setCompStartDate] = useState<any>(null);
    const [compEndDate, setCompEndDate] = useState<any>(null);
    const [payoutTransmitDate, setPayoutTransmitDate] = useState<any>(null);
    const [isActiveDisabled, setIsActiveDisabled] = useState<boolean>(true);
    const [disableCompStartDate, setDisableCompStartDate] = useState<boolean>(false);
    const [disableCompEndDate, setDisableCompEndDate] = useState<boolean>(false);
    const [disableMgrEndDate, setDisableMgrEndDate] = useState<boolean>(false);
    const [disableMgrStartDate, setDisableMgrStartDate] = useState<boolean>(false);
    const [disableTransmitStartDate, setDisableTransmitStartDate] = useState<boolean>(false);
    const [disableTLStartDate, setDisableTLStartDate] = useState<boolean>(false);
    const [disableAssociateStartDate, setDisableAssociateStartDate] = useState<boolean>(false);
    const [disableAssociateEndDate, setDisableAssociateEndDate] = useState<boolean>(false);
    const [disableTLEndDate, setDisableTLEndDate] = useState<boolean>(false);

    const openDateColClassName = (via === 'EdsDashboard' || via === 'ManagerDashboard') ? 'col-md-5' : 'col-md-2';
    const closeDateColClassName = (via === 'EdsDashboard' || via === 'ManagerDashboard') ? 'col-md-5' : 'col-md-2 offset-md-1';
    const buttonRowColClassName = (via === 'EdsDashboard' || via === 'ManagerDashboard') ? 'col-md-10' : 'col-md-5';

    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = prevMonth === 11 ? year - 1 : year;
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = nextMonth === 0 ? year + 1 : year;

    const minDate = new Date();

    minDate.setMonth(prevMonth);
    minDate.setFullYear(prevYear);

    const maxDate = new Date();

    maxDate.setMonth(nextMonth);
    maxDate.setFullYear(nextYear);

    const { data: primaryApprovalWindowData } = useGetPrimaryApprovalWindowQuery(
        {
            year: inputYear,
            month: inputMonth,
            scorecardVersionId: inputScorecardVersion,
            approvalWindowConfigLevelId: selectedConfigurationLevel,
        },
        {
            skip:
                !inputYear ||
                !inputMonth ||
                (!inputScorecardVersion && selectedConfigurationLevel === 2) ||
                !selectedConfigurationLevel,
        }
    );

    const UpdateInsertScorecardApprovalWindow = (reviewStatus: any) => {
        updateInsertScorecardApprovalWindow(reviewStatus)
            .unwrap()
            .then(() => {
                toast.success('Approval window updated successfully');
            })
            .then((error: any) => {
                if (error) {
                    toast.error('Approval window update failed');
                }
            });
    };

    useEffect(() => {
        if (
            associateStartDate === null ||
            associateEndDate === null ||
            tLStartDate === null ||
            tLEndDate === null ||
            mgrStartDate === null ||
            mgrEndDate === null ||
            compStartDate === null ||
            compEndDate === null ||
            payoutTransmitDate === null ||
            (inputScorecardVersion === null && selectedConfigurationLevel === 2)
        ) {
            setIsActiveDisabled(true);
        } else {
            setIsActiveDisabled(false);
        }
    }, [
        inputYear,
        inputMonth,
        inputScorecardVersion,
        associateStartDate,
        associateEndDate,
        tLStartDate,
        tLEndDate,
        mgrStartDate,
        mgrEndDate,
        compStartDate,
        compEndDate,
        payoutTransmitDate,
        selectedConfigurationLevel,
    ]);
    useEffect(() => {
        const windowData = primaryApprovalWindowData?.data[0];

        clearDates();
        manipulateAllDates(false);

        if (windowData) {
            const {
                associate_start_date,
                associate_end_date,
                team_lead_start_date,
                team_lead_end_date,
                manager_start_date,
                manager_end_date,
                comp_team_start_date,
                comp_team_end_date,
                payout_transmit_date,
            } = windowData;

            setAssociateStartDate(associate_start_date ? new Date(associate_start_date) : null);
            setAssociateEndDate(associate_end_date ? new Date(associate_end_date) : null);
            setTLStartDate(team_lead_start_date ? new Date(team_lead_start_date) : null);
            setTLEndDate(team_lead_end_date ? new Date(team_lead_end_date) : null);
            setMgrStartDate(manager_start_date ? new Date(manager_start_date) : null);
            setMgrEndDate(manager_end_date ? new Date(manager_end_date) : null);
            setCompStartDate(comp_team_start_date ? new Date(comp_team_start_date) : null);
            setCompEndDate(comp_team_end_date ? new Date(comp_team_end_date) : null);
            setPayoutTransmitDate(payout_transmit_date ? new Date(payout_transmit_date) : null);
        }

        if (inputReadonly === true) {
            manipulateAllDates(true);
        }
    }, [
        inputYear,
        inputMonth,
        inputScorecardVersion,
        inputReadonly,
        primaryApprovalWindowData,
        selectedConfigurationLevel,
    ]);

    const manipulateAllDates = (disable: boolean) => {
        setDisableAssociateEndDate(disable);
        setDisableAssociateStartDate(disable);
        setDisableTLStartDate(disable);
        setDisableTLEndDate(disable);
        setDisableMgrStartDate(disable);
        setDisableMgrEndDate(disable);
        setDisableCompStartDate(disable);
        setDisableCompEndDate(disable);
        setDisableTransmitStartDate(disable);
    };

    const clearDates = () => {
        setAssociateStartDate(null);
        setAssociateEndDate(null);
        setTLStartDate(null);
        setTLEndDate(null);
        setMgrStartDate(null);
        setMgrEndDate(null);
        setCompStartDate(null);
        setCompEndDate(null);
        setPayoutTransmitDate(null);
    };
    const changeAssociateEndDate = (e: any) => {
        setAssociateEndDate(e.value);
        setTLStartDate(getNextDate(e.value));
    };
    const changeTLStartDate = (e: any) => {
        setTLStartDate(e.value);
        setAssociateEndDate(getPreviuosDate(e.value));
    };

    const changeTLEndDate = (e: any) => {
        setTLEndDate(e.value);
        setMgrStartDate(getNextDate(e.value));
    };
    const changeMgrStartDate = (e: any) => {
        setMgrStartDate(e.value);
        setTLEndDate(getPreviuosDate(e.value));
    };

    const changeMgrEndDate = (e: any) => {
        setMgrEndDate(e.value);
        setCompStartDate(getNextDate(e.value));
    };
    const changeCompStartDate = (e: any) => {
        setCompStartDate(e.value);
        setMgrEndDate(getPreviuosDate(e.value));
    };
    const changeCompEndDate = (e: any) => {
        setCompEndDate(e.value);
        setPayoutTransmitDate(getNextDate(e.value));
    };
    const changePayoutTransmitDate = (e: any) => {
        setPayoutTransmitDate(e.value);
        setCompEndDate(getPreviuosDate(e.value));
    };
    const accept = () => {
        onActivateClick();
    };

    const reject = () => {
        toast.error('You have rejected');
    };
    const msg1 =
        'This approval dates will apply Globally except scorecards configurated individually or at Parent Client Level';
    const msg2 =
        'This approval dates will apply to the specific Parent Client only, except scorecards configurated individually';

    const confirmActivate = () => {
        confirmDialog({
            message: selectedConfigurationLevel === 1 ? msg1 : msg2,
            header: 'Status Update',
            icon: 'pi pi-info-circle',
            accept,
            reject,
        });
    };
    const onActivateClick = () => {
        setIsActiveDisabled(true);
        if (checkScorecardApprovalWindowValidation() && checkApprovalDates()) {
            const payload = {
                scorecard_version_id: selectedConfigurationLevel === 2 ? inputScorecardVersion : 0,
                year: inputYear,
                month: inputMonth,
                associate_start_date: datetimeToDate(associateStartDate),
                team_lead_start_date: datetimeToDate(tLStartDate),
                manager_start_date: datetimeToDate(mgrStartDate),
                comp_team_start_date: datetimeToDate(compStartDate),
                payout_transmit_date: datetimeToDate(payoutTransmitDate),
                approval_window_config_level_id: selectedConfigurationLevel,
                modify_by: modifyByEmpId,
            };
            UpdateInsertScorecardApprovalWindow(payload);
        }
        setIsActiveDisabled(false);
    };

    const checkScorecardApprovalWindowValidation = (): boolean => {
        if (
            (selectedConfigurationLevel === 2 &&
                (inputScorecardVersion === null ||
                    inputScorecardVersion === undefined ||
                    inputScorecardVersion <= 0)) ||
            inputYear === null ||
            inputYear === undefined ||
            inputYear <= 0 ||
            inputMonth === null ||
            inputMonth === undefined ||
            inputMonth <= 0
        ) {
            toast.error('Please select scorecard filter inputs above.');
            return false;
        }
        return true;
    };
    const checkApprovalDates = (): boolean => {
        try {
            if (associateStartDate.getTime() > associateEndDate.getTime()) {
                toast.error('Please check the Associate approval dates range');
                return false;
            }
            if (tLStartDate.getTime() > tLEndDate.getTime()) {
                toast.error('Please check the Team Lead approval dates range');
                return false;
            }

            if (mgrStartDate.getTime() > mgrEndDate.getTime()) {
                toast.error('Please check the Manager approval dates range');
                return false;
            }
            if (compStartDate.getTime() > compEndDate.getTime()) {
                toast.error('Please check the Compensation approval dates range');
                return false;
            }
            if (mgrEndDate.getTime() > payoutTransmitDate.getTime()) {
                toast.error('Please check the Transmit approval dates range');
                return false;
            }

            return true;
        } catch (error) {
            toast.error('Please check the approval dates range');
            return false;
        }
    };

    if (selectedConfigurationLevel > 0) {
        return (
            <div className="card shadow border-0 h-100 borderRadius10 PrimaryApprovalDefault">
                <ConfirmDialog />
                <div className={`card-body `}>
                    <h6 className="card-title">
                        <b>Pay Period</b> :
                        <i style={{ fontWeight: 'normal' }}>
                            {' '}
                            {monthNames[inputMonth ?? 0]} {inputYear}{' '}
                        </i>
                    </h6>
                    Configuration Level :{' '}
                    <Dropdown
                        value={selectedConfigurationLevel}
                        onChange={(e: any) => setSelectedConfigurationLevel(e.value)}
                        options={configurationLevels}
                        optionLabel="name"
                        optionValue={'code'}
                        placeholder="Select a Configuration"
                        className="custom-dropdown"
                    />
                    <hr className="mt-3" />
                    <div className="row mt-3">
                        <div className="col-md-2 pe-0">&nbsp;</div>
                        <div className={openDateColClassName}>Open Date</div>
                        <div className={closeDateColClassName}>Close Date</div>
                    </div>
                    <div className="row mt-4">
                        <div className="col-md-2 pe-0 pt-2">Associate</div>
                        <div className={openDateColClassName}>
                            <Calendar
                                id="icon"
                                value={associateStartDate}
                                dateFormat="dd-M-yy"
                                onChange={(e: any) => setAssociateStartDate(e.value)}
                                showIcon
                                disabled={disableAssociateStartDate}
                                className={`custom-calendar ${associateStartDate === null ? 'p-invalid' : ''}`}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        </div>
                        <div className={closeDateColClassName}>
                            <Calendar
                                id="icon"
                                value={associateEndDate}
                                dateFormat="dd-M-yy"
                                disabled={disableAssociateEndDate}
                                onChange={changeAssociateEndDate}
                                showIcon
                                className={`custom-calendar ${associateEndDate === null ? 'p-invalid' : ''}`}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-2 pe-0 pt-2">Team Lead</div>
                        <div className={openDateColClassName}>
                            <Calendar
                                id="icon"
                                value={tLStartDate}
                                dateFormat="dd-M-yy"
                                disabled={disableTLStartDate}
                                onChange={changeTLStartDate}
                                showIcon
                                className={`custom-calendar ${tLStartDate === null ? 'p-invalid' : ''}`}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        </div>
                        <div className={closeDateColClassName}>
                            <Calendar
                                id="icon"
                                value={tLEndDate}
                                dateFormat="dd-M-yy"
                                onChange={changeTLEndDate}
                                disabled={disableTLEndDate}
                                showIcon
                                className={`custom-calendar ${tLEndDate === null ? 'p-invalid' : ''}`}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-2 pe-0 pt-2">Manager</div>
                        <div className={openDateColClassName}>
                            <Calendar
                                disabled={disableMgrStartDate}
                                id="icon"
                                value={mgrStartDate}
                                dateFormat="dd-M-yy"
                                onChange={changeMgrStartDate}
                                showIcon
                                className={`custom-calendar ${mgrStartDate === null ? 'p-invalid' : ''}`}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        </div>
                        <div className={closeDateColClassName}>
                            <Calendar
                                disabled={disableMgrEndDate}
                                id="icon"
                                value={mgrEndDate}
                                dateFormat="dd-M-yy"
                                onChange={changeMgrEndDate}
                                showIcon
                                className={`custom-calendar ${mgrEndDate === null ? 'p-invalid' : ''}`}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-2 pe-0 pt-2">Comp</div>
                        <div className={openDateColClassName}>
                            <Calendar
                                disabled={disableCompStartDate}
                                id="icon"
                                value={compStartDate}
                                dateFormat="dd-M-yy"
                                onChange={changeCompStartDate}
                                showIcon
                                className={`custom-calendar ${compStartDate === null ? 'p-invalid' : ''}`}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        </div>
                        <div className={closeDateColClassName}>
                            <Calendar
                                id="icon"
                                value={compEndDate}
                                dateFormat="dd-M-yy"
                                onChange={changeCompEndDate}
                                showIcon
                                className={`custom-calendar ${compEndDate === null ? 'p-invalid' : ''}`}
                                minDate={minDate}
                                maxDate={maxDate}
                                disabled={disableCompEndDate}
                            />
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-2 pe-0 pt-2">Transmit</div>
                        <div className={openDateColClassName}>
                            <Calendar
                                id="icon"
                                value={payoutTransmitDate}
                                dateFormat="dd-M-yy"
                                onChange={changePayoutTransmitDate}
                                showIcon
                                className={`custom-calendar ${payoutTransmitDate === null ? 'p-invalid' : ''}`}
                                minDate={minDate}
                                maxDate={maxDate}
                                disabled={disableTransmitStartDate}
                            />
                        </div>
                    </div>
                    {inputReadonly ? (
                        <div className="row mt-2">&nbsp;</div>
                    ) : (
                        <div className="row mt-3">
                            <div className="col-md-2 pe-0">&nbsp;</div>
                            <div className={`${buttonRowColClassName} d-flex justify-content-end flex-wrap gap-2`}>
                                <Button
                                    className="btn-secondary"
                                    style={{ minWidth: '140px' }}
                                    onClick={clearDates}
                                    label="Cancel"
                                />
                                {selectedConfigurationLevel === 1 && (
                                    <Button
                                        disabled={isActiveDisabled}
                                        className="btn-primary"
                                        style={{ minWidth: '150px' }}
                                        onClick={confirmActivate}
                                        label={'Activate Globally'}
                                    />
                                )}

                                {selectedConfigurationLevel === 2 && (
                                    <Button
                                        disabled={isActiveDisabled}
                                        className="btn-primary"
                                        style={{ minWidth: '150px' }}
                                        onClick={confirmActivate}
                                        label={'Activate for Parent Client'}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="col-12">
            {/* <h6 className="card-title">Primary Approval Windows</h6> */}
            <hr className="mt-2" />
            <MessageInfo
                title="No results found."
                message="Try adjusting the month or scorecard filter above."
                rowHeight="308px"
            />
        </div>
    );
};

export default PrimaryApprovalDefault;
